import { supabase } from './supabase.js'

// Tiny utility for uploading a photo to the receive-photos Storage
// bucket and returning the stored object PATH (the bucket is private as
// of migration 0058, so there is no public URL to build).
//
// Why a helper instead of inline upload code in the form: keeps the
// form focused on form logic, and lets us swap implementations later
// (e.g. resize on the client) without touching the form.
//
// Paths are scoped per restaurant: receives/<restaurant_id>/<uuid>.<ext>.
// The restaurant_id segment is what the storage RLS policy and the
// signing / extract edge functions authorize against, so callers MUST
// pass the active restaurant id.

const BUCKET = 'receive-photos'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Build the per-restaurant object path. Exported so it can be unit
// tested without touching Storage. Throws a friendly error BEFORE any
// network call if the restaurant id is missing or malformed.
export function buildReceivePhotoPath(restaurantId, ext) {
  if (!restaurantId || typeof restaurantId !== 'string' || !UUID_RE.test(restaurantId)) {
    throw new Error('Could not attach photo: no restaurant selected. Try reopening this screen.')
  }
  // crypto.randomUUID — supported in all modern browsers and Safari 15+.
  return `receives/${restaurantId}/${crypto.randomUUID()}.${ext}`
}

// Upload a File (from <input type="file">) to Supabase Storage.
// Returns the stored object PATH string on success, throws on failure.
export async function uploadReceivePhoto(file, restaurantId) {
  if (!file) throw new Error('No file provided')
  if (!file.type?.startsWith('image/')) {
    throw new Error('Only image files are supported')
  }

  // Validate the restaurant scope FIRST so we never upload an
  // unauthorizable, RLS-denied object.
  const ext = guessExtension(file)
  const path = buildReceivePhotoPath(restaurantId, ext)

  const { error } = await supabase
    .storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      contentType: file.type || 'image/jpeg',
      upsert: false,
    })
  if (error) throw error

  // Private bucket → return the path. Reads go through the
  // sign-receive-photo edge function, which mints a short-TTL signed URL.
  return path
}

// Best-effort extension inference. The browser's File.name may or may
// not include one, so fall back to the MIME type then default to jpg.
function guessExtension(file) {
  const fromName = (file.name || '').split('.').pop()
  if (fromName && fromName.length <= 5 && /^[a-z0-9]+$/i.test(fromName)) {
    return fromName.toLowerCase()
  }
  const mime = (file.type || '').toLowerCase()
  if (mime.includes('png'))  return 'png'
  if (mime.includes('webp')) return 'webp'
  if (mime.includes('heic')) return 'heic'
  if (mime.includes('heif')) return 'heif'
  return 'jpg'
}
